import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * NOTE (auth token storage):
 * We store the short-lived JWT (30-day exp) in localStorage. This is the standard
 * pattern for stateless SPA auth with a same-origin API. The main mitigation
 * against XSS-based token theft is: (1) no untrusted third-party scripts on
 * critical routes, (2) React auto-escaped output, and (3) strict CSP at the
 * ingress. Switching to httpOnly cookies would require CSRF handling and a
 * cookie-based backend session — tracked in the backlog.
 */

const AppContext = createContext(null);

const readCartFromStorage = () => {
  try { return JSON.parse(localStorage.getItem("as_cart") || "[]"); }
  catch { return []; }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(() => !!localStorage.getItem("as_token"));
  const [lang, setLang] = useState(() => localStorage.getItem("as_lang") || "en");
  const [cart, setCart] = useState(readCartFromStorage);
  const [languages, setLanguages] = useState([]);

  // Bootstrap: fetch languages + auth (runs once on mount by design).
  useEffect(() => {
    api.get("/meta/languages").then((r) => setLanguages(r.data)).catch(() => {});
    const token = localStorage.getItem("as_token");
    if (token) {
      api.get("/auth/me")
        .then((r) => setUser(r.data))
        .catch(() => { localStorage.removeItem("as_token"); })
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []);

  useEffect(() => { localStorage.setItem("as_cart", JSON.stringify(cart)); }, [cart]);
  useEffect(() => { localStorage.setItem("as_lang", lang); }, [lang]);

  const login = useCallback((token, u) => {
    localStorage.setItem("as_token", token);
    setUser(u);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("as_token");
    setUser(null);
  }, []);

  const addToCart = useCallback((product, qty = 1) => {
    setCart((c) => {
      const idx = c.findIndex((i) => i.sku === product.sku);
      if (idx >= 0) {
        const copy = [...c];
        copy[idx] = { ...copy[idx], quantity: Math.min(99, copy[idx].quantity + qty) };
        return copy;
      }
      return [...c, { sku: product.sku, name: product.name, price: product.price, image: product.image, quantity: qty }];
    });
  }, []);

  const updateQty = useCallback((sku, qty) => {
    setCart((c) => c.map((i) => i.sku === sku ? { ...i, quantity: Math.max(1, Math.min(99, qty)) } : i));
  }, []);

  const removeItem = useCallback((sku) => {
    setCart((c) => c.filter((i) => i.sku !== sku));
  }, []);

  const clearCart = useCallback(() => setCart([]), []);

  const cartTotal = useMemo(() => cart.reduce((s, i) => s + i.price * i.quantity, 0), [cart]);
  const cartCount = useMemo(() => cart.reduce((s, i) => s + i.quantity, 0), [cart]);

  const value = useMemo(() => ({
    user, authLoading, login, logout,
    lang, setLang, languages,
    cart, addToCart, updateQty, removeItem, clearCart, cartTotal, cartCount,
  }), [
    user, authLoading, login, logout,
    lang, languages,
    cart, addToCart, updateQty, removeItem, clearCart, cartTotal, cartCount,
  ]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
};
